import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getLiteratureReview } from "../Utils/functions";

const LiteratureReviewReportContext = createContext(null);

export function useLiteratureReviewReport() {
  return useContext(LiteratureReviewReportContext);
}

export function jobHasSavedReport(job) {
  if (!job) return false;
  const s = job.synthesisResult;
  if (s == null || s === "") return false;
  if (typeof s === "string") return s.trim().length > 0;
  if (Array.isArray(s)) return s.length > 0;
  if (typeof s === "object") return Object.keys(s).length > 0;
  return Boolean(s);
}

export function LiteratureReviewReportProvider({ reviewId, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [hasSavedReport, setHasSavedReport] = useState(false);

  const hashStep = (location.hash || "").replace(/^#/, "").trim();
  const routeStep = hashStep || location.state?.step || null;

  const loadReportFlag = useCallback(async () => {
    if (!reviewId) {
      setHasSavedReport(false);
      return;
    }
    try {
      const job = await getLiteratureReview(reviewId);
      setHasSavedReport(jobHasSavedReport(job));
    } catch {
      setHasSavedReport(false);
    }
  }, [reviewId]);

  useEffect(() => {
    loadReportFlag();
  }, [loadReportFlag, location.hash, location.pathname]);

  const goToReport = useCallback(() => {
    if (!reviewId) return;
    navigate(`/screening/${reviewId}#research-report`, {
      state: { ...(location.state || {}), step: "research-report" },
    });
  }, [navigate, reviewId, location.state]);

  const refreshReportAvailability = useCallback(() => {
    loadReportFlag();
  }, [loadReportFlag]);

  const value = useMemo(
    () => ({
      hasSavedReport,
      goToReport,
      refreshReportAvailability,
      routeStep,
    }),
    [hasSavedReport, goToReport, refreshReportAvailability, routeStep]
  );

  return (
    <LiteratureReviewReportContext.Provider value={value}>
      {children}
    </LiteratureReviewReportContext.Provider>
  );
}
